import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Plus, Trash2, Save, X, ImagePlus, Package, Receipt } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import imageCompression from 'browser-image-compression';
import api, { inventoryAPI, usersAPI } from '@/lib/api';

export default function WorkOrderForm({ ticket, workOrder: existingWorkOrder, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [workOrder, setWorkOrder] = useState(null);
  const customerCanvasRef = useRef(null);
  const techCanvasRef = useRef(null);
  const [customerSigPad, setCustomerSigPad] = useState(null);
  const [techSigPad, setTechSigPad] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [hourlyRate, setHourlyRate] = useState(null);
  const [technicians, setTechnicians] = useState([]);

  const [formData, setFormData] = useState({
    address: '',
    person_called: '',
    work_done: '',
    remote_onsite: 'Onsite',
    time_spent_hours: 1,  // default 1 hour for onsite
    parts_used: [],
    photos: [],
    inventory_items: [],
    technician_id: '',
  });

  const [newPart, setNewPart] = useState({ name: '', quantity: 1, cost: 0 });
  const [compressing, setCompressing] = useState(false);
  const photoInputRef = useRef(null);

  useEffect(() => {
    if (existingWorkOrder) {
      setWorkOrder(existingWorkOrder);
      setFormData({
        address: existingWorkOrder.address || '',
        person_called: existingWorkOrder.person_called || '',
        work_done: existingWorkOrder.work_done || '',
        remote_onsite: existingWorkOrder.remote_onsite || 'Onsite',
        time_spent_hours: existingWorkOrder.time_spent_hours ?? 1,
        parts_used: existingWorkOrder.parts_used || [],
        photos: existingWorkOrder.photos || [],
        inventory_items: existingWorkOrder.inventory_items || [],
        technician_id: existingWorkOrder.technician_id || '',
      });
      if (existingWorkOrder.hourly_rate != null) setHourlyRate(existingWorkOrder.hourly_rate);
    } else {
      // Load work order for this ticket
      loadWorkOrder();
    }
    // Load inventory for the picker
    // Load inventory for the parts/assets picker. The api exports `listItems`
    // (not `list`); using the wrong name surfaces as "inventoryAPI.list is not
    // a function" → React swallows the render and the screen goes blank.
    inventoryAPI.listItems().then((res) => setInventory(res.data || [])).catch(() => {});
    // Load agent/admin list so the "Technician" field can be a dropdown
    // rather than silently defaulting to the logged-in user.
    Promise.all([
      usersAPI.list('agent').catch(() => ({ data: [] })),
      usersAPI.list('admin').catch(() => ({ data: [] })),
      usersAPI.list('manager').catch(() => ({ data: [] })),
    ]).then(([a, ad, m]) => {
      const merged = [...(a.data || []), ...(ad.data || []), ...(m.data || [])];
      // De-duplicate by id (admin tier-3 users may appear in both lists)
      const seen = new Set();
      const uniq = merged.filter((u) => {
        if (seen.has(u.id)) return false;
        seen.add(u.id);
        return true;
      });
      uniq.sort((x, y) => (x.name || '').localeCompare(y.name || ''));
      setTechnicians(uniq);
    });
  }, [ticket.id, existingWorkOrder]);

  useEffect(() => {
    // Initialize signature pads (one for the customer, one for the
    // technician). signature_pad is a single-canvas helper, so we own two
    // independent instances. They render on different canvases (refs above)
    // and are saved separately on submit.
    let cancelled = false;
    import('signature_pad').then((module) => {
      if (cancelled) return;
      const SignaturePad = module.default;
      if (customerCanvasRef.current && !customerSigPad) {
        setCustomerSigPad(new SignaturePad(customerCanvasRef.current, {
          backgroundColor: 'rgb(255, 255, 255)',
          penColor: 'rgb(0, 0, 0)',
        }));
      }
      if (techCanvasRef.current && !techSigPad) {
        setTechSigPad(new SignaturePad(techCanvasRef.current, {
          backgroundColor: 'rgb(255, 255, 255)',
          penColor: 'rgb(0, 0, 0)',
        }));
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerCanvasRef.current, techCanvasRef.current]);

  const [loadedCustomerSig, setLoadedCustomerSig] = useState(null); // existing data URL
  const [loadedTechSig, setLoadedTechSig] = useState(null);

  const loadWorkOrder = async () => {
    try {
      const res = await api.get(`/work-orders?ticket_id=${ticket.id}`);
      if (res.data && res.data.length > 0) {
        const wo = res.data[0];
        setWorkOrder(wo);
        setFormData({
          address: wo.address || '',
          person_called: wo.person_called || '',
          work_done: wo.work_done || '',
          remote_onsite: wo.remote_onsite || 'Onsite',
          time_spent_hours: wo.time_spent_hours ?? 1,
          parts_used: wo.parts_used || [],
          photos: wo.photos || [],
          inventory_items: wo.inventory_items || [],
          technician_id: wo.technician_id || '',
        });
        if (wo.hourly_rate != null) setHourlyRate(wo.hourly_rate);
        // Display previously-captured signatures as preview images alongside
        // the pads. We keep them out of the canvas so each pad starts blank
        // for new strokes; the previous data URL is sent back unchanged on
        // submit if the pad is left empty.
        setLoadedCustomerSig(wo.customer_signature || wo.signature_image || null);
        setLoadedTechSig(wo.technician_signature || null);
      }
    } catch (err) {
      console.error('Failed to load work order:', err);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setCompressing(true);
    try {
      const options = {
        maxSizeMB: 1.2,
        maxWidthOrHeight: 1600,
        useWebWorker: true,
        initialQuality: 0.5,
        fileType: 'image/jpeg',
      };
      const dataUrls = await Promise.all(
        files.map(async (file) => {
          try {
            const compressed = await imageCompression(file, options);
            return await imageCompression.getDataUrlFromFile(compressed);
          } catch (err) {
            console.error('Compress failed:', err);
            return await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(file);
            });
          }
        })
      );
      setFormData((prev) => ({ ...prev, photos: [...prev.photos, ...dataUrls] }));
    } finally {
      setCompressing(false);
      if (e.target) e.target.value = '';
    }
  };

  const removePhoto = (idx) => {
    setFormData((prev) => ({ ...prev, photos: prev.photos.filter((_, i) => i !== idx) }));
  };

  const addPart = () => {
    if (newPart.name && newPart.quantity > 0) {
      setFormData({
        ...formData,
        parts_used: [...formData.parts_used, { ...newPart }],
      });
      setNewPart({ name: '', quantity: 1, cost: 0 });
    }
  };

  const removePart = (index) => {
    setFormData({
      ...formData,
      parts_used: formData.parts_used.filter((_, i) => i !== index),
    });
  };

  const addInventoryItem = (inv) => {
    if (!inv) return;
    if (formData.inventory_items.some((i) => i.inventory_id === inv.id)) return;  // already added
    setFormData((prev) => ({
      ...prev,
      inventory_items: [...prev.inventory_items, {
        inventory_id: inv.id,
        name: inv.name,
        sku: inv.sku || null,
        quantity: 1,
        unit_price: parseFloat(inv.unit_price ?? inv.price ?? 0),
      }],
    }));
  };

  const updateInvQty = (idx, qty) => {
    const q = Math.max(1, parseInt(qty || '1', 10));
    setFormData((prev) => ({
      ...prev,
      inventory_items: prev.inventory_items.map((it, i) => i === idx ? { ...it, quantity: q } : it),
    }));
  };

  const removeInventoryItem = (idx) => {
    setFormData((prev) => ({
      ...prev,
      inventory_items: prev.inventory_items.filter((_, i) => i !== idx),
    }));
  };

  const clearCustomerSignature = () => { if (customerSigPad) customerSigPad.clear(); };
  const clearTechSignature = () => { if (techSigPad) techSigPad.clear(); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Capture each pad independently. If the pad is left untouched but a
      // signature was loaded earlier (editing an existing WO), preserve it so
      // a save doesn't accidentally wipe a previously-captured signature.
      const customer_signature =
        customerSigPad && !customerSigPad.isEmpty()
          ? customerSigPad.toDataURL()
          : loadedCustomerSig;
      const technician_signature =
        techSigPad && !techSigPad.isEmpty()
          ? techSigPad.toDataURL()
          : loadedTechSig;

      const payload = {
        ticket_id: ticket.id,
        address: formData.address,
        person_called: formData.person_called,
        work_done: formData.work_done,
        remote_onsite: formData.remote_onsite,
        time_spent_hours: parseFloat(formData.time_spent_hours),
        parts_used: formData.parts_used,
        customer_signature,
        technician_signature,
        photos: formData.photos,
        inventory_items: formData.inventory_items,
        technician_id: formData.technician_id || undefined,
      };

      await api.post('/work-orders', payload);
      
      if (onSaved) onSaved();
      if (onClose) onClose();
    } catch (err) {
      console.error('Failed to save work order:', err);
      alert('Failed to save work order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const totalCost = formData.parts_used.reduce(
    (sum, part) => sum + part.cost * part.quantity,
    0
  );

  const inventoryTotal = formData.inventory_items.reduce(
    (sum, it) => sum + (parseFloat(it.unit_price) || 0) * (parseInt(it.quantity, 10) || 0),
    0
  );

  const laborTotal = (hourlyRate || 0) * (parseFloat(formData.time_spent_hours) || 0);
  const grandTotal = totalCost + inventoryTotal + laborTotal;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-auto">
      <Card className="bg-white border-slate-200 w-full max-w-3xl max-h-[90vh] overflow-auto">
        <CardHeader className="border-b border-slate-200 sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-slate-800">Work Order Form</CardTitle>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant="outline" className="text-slate-600">
                  {ticket.ticket_number}
                </Badge>
                <span className="text-sm text-slate-500">{ticket.subject}</span>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Customer Info (Read-only) */}
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <h3 className="font-semibold text-slate-800 mb-3">Customer Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Customer Name</Label>
                  <p className="text-sm font-medium text-slate-800">{ticket.customer_name}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Email</Label>
                  <p className="text-sm text-slate-600">{ticket.customer_email}</p>
                </div>
              </div>
            </div>

            {/* Editable Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700">Address</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Service location address"
                  className="bg-white border-slate-200"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-700">Person Called / Contact</Label>
                <Input
                  value={formData.person_called}
                  onChange={(e) => setFormData({ ...formData, person_called: e.target.value })}
                  placeholder="Name of person contacted"
                  className="bg-white border-slate-200"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700">Service Type</Label>
                <Select
                  value={formData.remote_onsite}
                  onValueChange={(value) => setFormData({ ...formData, remote_onsite: value })}
                >
                  <SelectTrigger className="bg-white border-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Remote">Remote</SelectItem>
                    <SelectItem value="Onsite">Onsite</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-700">Total Time to Fix (hours)</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  value={formData.time_spent_hours}
                  onChange={(e) => setFormData({ ...formData, time_spent_hours: e.target.value })}
                  className="bg-white border-slate-200"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700">Technician (performed by)</Label>
              <Select
                value={formData.technician_id || 'self'}
                onValueChange={(value) => setFormData({ ...formData, technician_id: value === 'self' ? '' : value })}
              >
                <SelectTrigger className="bg-white border-slate-200" data-testid="wo-technician-select">
                  <SelectValue placeholder="Select technician" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="self">— Use logged-in user —</SelectItem>
                  {technicians.map((t) => (
                    <SelectItem key={t.id} value={t.id} data-testid={`wo-tech-option-${t.id}`}>
                      {t.name}{t.role ? ` (${t.role})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500">
                Pick whichever tech actually performed the work — this name appears on the PDF/email.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700">Work Details / Description</Label>
              <Textarea
                value={formData.work_done}
                onChange={(e) => setFormData({ ...formData, work_done: e.target.value })}
                placeholder="Describe the work performed, issues found, and solutions applied..."
                className="bg-white border-slate-200 min-h-[120px]"
                required
              />
            </div>

            {/* Parts Used */}
            <div className="space-y-3">
              <Label className="text-slate-700">Parts Used</Label>
              
              <div className="grid grid-cols-12 gap-2">
                <Input
                  placeholder="Part name"
                  value={newPart.name}
                  onChange={(e) => setNewPart({ ...newPart, name: e.target.value })}
                  className="col-span-5 bg-white border-slate-200"
                />
                <Input
                  type="number"
                  placeholder="Qty"
                  min="1"
                  value={newPart.quantity}
                  onChange={(e) => setNewPart({ ...newPart, quantity: parseInt(e.target.value) || 1 })}
                  className="col-span-3 bg-white border-slate-200"
                />
                <Input
                  type="number"
                  placeholder="Cost"
                  step="0.01"
                  min="0"
                  value={newPart.cost}
                  onChange={(e) => setNewPart({ ...newPart, cost: parseFloat(e.target.value) || 0 })}
                  className="col-span-3 bg-white border-slate-200"
                />
                <Button type="button" onClick={addPart} size="icon" className="col-span-1 bg-cyan-500 hover:bg-cyan-600">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {formData.parts_used.length > 0 && (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-slate-600">Part</th>
                        <th className="px-3 py-2 text-center text-slate-600">Qty</th>
                        <th className="px-3 py-2 text-right text-slate-600">Cost</th>
                        <th className="px-3 py-2 text-right text-slate-600">Total</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {formData.parts_used.map((part, index) => (
                        <tr key={index}>
                          <td className="px-3 py-2 text-slate-800">{part.name}</td>
                          <td className="px-3 py-2 text-center text-slate-600">{part.quantity}</td>
                          <td className="px-3 py-2 text-right text-slate-600">${part.cost.toFixed(2)}</td>
                          <td className="px-3 py-2 text-right font-medium text-slate-800">
                            ${(part.cost * part.quantity).toFixed(2)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removePart(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-slate-50 font-semibold">
                        <td colSpan="3" className="px-3 py-2 text-right text-slate-700">Total Cost:</td>
                        <td className="px-3 py-2 text-right text-slate-800">${totalCost.toFixed(2)}</td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Inventory Items (auto-priced) */}
            <div className="space-y-3">
              <Label className="text-slate-700 flex items-center gap-2">
                <Package className="w-4 h-4 text-slate-400" />Inventory items
                <span className="text-xs text-slate-400 font-normal">(prices pulled automatically)</span>
              </Label>
              <Select
                value=""
                onValueChange={(invId) => {
                  const inv = inventory.find((i) => i.id === invId);
                  if (inv) addInventoryItem(inv);
                }}
              >
                <SelectTrigger className="bg-white border-slate-200 h-11" data-testid="wo-inventory-select" aria-label="Add inventory item">
                  <SelectValue placeholder={inventory.length === 0 ? 'Loading inventory...' : 'Select an inventory item to attach'} />
                </SelectTrigger>
                <SelectContent>
                  {inventory.map((inv) => (
                    <SelectItem key={inv.id} value={inv.id}>
                      {inv.name}{inv.sku ? ` · ${inv.sku}` : ''} {inv.unit_price != null ? `— $${parseFloat(inv.unit_price).toFixed(2)}` : (inv.price != null ? `— $${parseFloat(inv.price).toFixed(2)}` : '')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {formData.inventory_items.length > 0 && (
                <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="wo-inventory-table">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-slate-600">Item</th>
                        <th className="px-3 py-2 text-center text-slate-600">Qty</th>
                        <th className="px-3 py-2 text-right text-slate-600">Unit price</th>
                        <th className="px-3 py-2 text-right text-slate-600">Line total</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {formData.inventory_items.map((it, idx) => (
                        <tr key={`${it.inventory_id}-${idx}`} data-testid={`wo-inv-row-${idx}`}>
                          <td className="px-3 py-2 text-slate-800">
                            <div className="font-medium">{it.name}</div>
                            {it.sku && <div className="text-xs text-slate-500">{it.sku}</div>}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <Input
                              type="number"
                              min="1"
                              value={it.quantity}
                              onChange={(e) => updateInvQty(idx, e.target.value)}
                              className="h-8 w-16 mx-auto text-center bg-white border-slate-200"
                              data-testid={`wo-inv-qty-${idx}`}
                            />
                          </td>
                          <td className="px-3 py-2 text-right text-slate-600">${(parseFloat(it.unit_price) || 0).toFixed(2)}</td>
                          <td className="px-3 py-2 text-right font-medium text-slate-800">${((parseFloat(it.unit_price) || 0) * (parseInt(it.quantity, 10) || 0)).toFixed(2)}</td>
                          <td className="px-3 py-2 text-right">
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeInventoryItem(idx)} className="text-red-500 hover:text-red-700" data-testid={`wo-inv-remove-${idx}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-slate-50 font-semibold">
                        <td colSpan="3" className="px-3 py-2 text-right text-slate-700">Inventory subtotal:</td>
                        <td className="px-3 py-2 text-right text-slate-800">${inventoryTotal.toFixed(2)}</td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Totals box */}
            {(hourlyRate != null || formData.parts_used.length > 0 || formData.inventory_items.length > 0) && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-1 text-sm" data-testid="wo-totals-box">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600 inline-flex items-center gap-1.5">
                    <Receipt className="w-4 h-4 text-slate-400" />
                    Hourly rate ({formData.remote_onsite}) {hourlyRate != null ? `× ${formData.time_spent_hours || 0}h` : ''}
                  </span>
                  <span className="font-medium text-slate-800">{hourlyRate != null ? `$${laborTotal.toFixed(2)}` : 'No client rate set'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Parts</span>
                  <span className="font-medium text-slate-800">${totalCost.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Inventory</span>
                  <span className="font-medium text-slate-800">${inventoryTotal.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between pt-2 mt-2 border-t border-slate-200">
                  <span className="text-slate-700 font-semibold">Estimated total</span>
                  <span className="text-lg font-bold text-slate-900" data-testid="wo-grand-total">${grandTotal.toFixed(2)}</span>
                </div>
              </div>
            )}

            {/* Photos Upload */}
            <div className="space-y-2">
              <Label className="text-slate-700">Photos (compressed 50%)</Label>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handlePhotoUpload}
                data-testid="wo-photo-input"
              />
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => photoInputRef.current?.click()}
                  disabled={compressing}
                  className="border-slate-300 text-slate-700"
                  data-testid="wo-add-photo-btn"
                >
                  {compressing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <ImagePlus className="w-4 h-4 mr-2" />
                  )}
                  Add Photos
                </Button>
                {formData.photos.length > 0 && (
                  <span className="text-xs text-slate-500">{formData.photos.length} photo(s) attached</span>
                )}
              </div>
              {formData.photos.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2" data-testid="wo-photos-preview">
                  {formData.photos.map((photo, idx) => (
                    <div key={idx} className="relative group">
                      <img
                        src={photo}
                        alt={`work-order-photo-${idx}`}
                        className="w-24 h-24 object-cover rounded-lg border border-slate-200"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(idx)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                        aria-label="Remove photo"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Signature Pads — two independent canvases. The customer pad
                is the legally important one (the customer authorising the
                work); the technician pad records who performed the work. */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2" data-testid="customer-signature-block">
                <Label className="text-slate-700">Customer Signature</Label>
                {loadedCustomerSig && (
                  <div className="text-[11px] text-slate-500 flex items-center gap-2 mb-1" data-testid="customer-signature-saved">
                    <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">Saved</span>
                    <img src={loadedCustomerSig} alt="saved customer signature" className="h-8 border border-slate-200 rounded bg-white" />
                    <span>signing again will replace it</span>
                  </div>
                )}
                <div className="border-2 border-slate-300 rounded-lg overflow-hidden bg-white">
                  <canvas
                    ref={customerCanvasRef}
                    width={600}
                    height={180}
                    className="w-full touch-none"
                    style={{ touchAction: 'none' }}
                  />
                </div>
                <div className="flex justify-between items-center">
                  <p className="text-[11px] text-slate-500">Have the customer sign here to authorise this work order.</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => { clearCustomerSignature(); setLoadedCustomerSig(null); }}
                    className="text-slate-600"
                    data-testid="clear-customer-signature-btn"
                  >
                    Clear
                  </Button>
                </div>
              </div>

              <div className="space-y-2" data-testid="technician-signature-block">
                <Label className="text-slate-700">Technician Signature</Label>
                {loadedTechSig && (
                  <div className="text-[11px] text-slate-500 flex items-center gap-2 mb-1" data-testid="technician-signature-saved">
                    <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">Saved</span>
                    <img src={loadedTechSig} alt="saved technician signature" className="h-8 border border-slate-200 rounded bg-white" />
                    <span>signing again will replace it</span>
                  </div>
                )}
                <div className="border-2 border-slate-300 rounded-lg overflow-hidden bg-white">
                  <canvas
                    ref={techCanvasRef}
                    width={600}
                    height={180}
                    className="w-full touch-none"
                    style={{ touchAction: 'none' }}
                  />
                </div>
                <div className="flex justify-between items-center">
                  <p className="text-[11px] text-slate-500">Sign here to confirm you performed this work.</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => { clearTechSignature(); setLoadedTechSig(null); }}
                    className="text-slate-600"
                    data-testid="clear-tech-signature-btn"
                  >
                    Clear
                  </Button>
                </div>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-cyan-500 hover:bg-cyan-600 rounded-full"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Work Order
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
